import { useLoaderData } from '@remix-run/react'
import { Colors } from '~/styles/colors'
import { getArticlesByAuthor } from '~/queries/getArticle'
import { BlogPaginate } from '~/components/blog/BlogPaginate'
import { Blog } from '~/components/blog/Blog'
import { Layout } from '~/components/Layout'
import { getCategories } from '~/queries/getCategories'
import type { LoaderFunction, MetaFunction } from '@remix-run/node'
import { AuthorHero } from '~/components/Author/AuthorHero/AuthorHero'
import { PostsPreviewGroup } from '~/components/blog/PostsPreviewGroup'

import { CIOEntries } from '~/libs/contentful/types/entries'
import {
  PageContext,
  preparePageContext,
  prepareRemixRequest,
  prepareRobotsMetadata,
} from '~/utils/routes/routeHelper'
import ContentfulClientManager from '../libs/contentful/ContentfulClientManager'
import {
  TypeArticleSkeleton,
  TypeAuthor,
  TypeAuthorSkeleton,
  TypeCategorySorting,
} from '~/types/contentful'
import { EntryCollection } from 'contentful'

const PAGE_LIMIT = 15

type AuthorLoaderData = {
  categoryEntries: TypeCategorySorting<'WITHOUT_UNRESOLVABLE_LINKS', string>
  authorData: TypeAuthor<'WITHOUT_UNRESOLVABLE_LINKS', string>
  authorPosts?: EntryCollection<TypeArticleSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  contextValue: PageContext
}

export const loader: LoaderFunction = async ({ params, request }) => {
  if (!params.slug) {
    throw new Response('Not Found', { status: 404 })
  }

  const url = prepareRemixRequest(request)
  const contextValue = preparePageContext(url)
  const page = contextValue.url.searchParams['page']

  const categoryEntries = await getCategories()

  const authorEntries = await ContentfulClientManager.getContentfulEntries<TypeAuthorSkeleton>(
    CIOEntries.author,
    {
      'fields.slug': params.slug,
      include: 3,
    },
  )

  const authorPosts = await getArticlesByAuthor({
    author: params.slug,
    page: Number(page || '1'),
    perPage: PAGE_LIMIT,
  })

  return {
    categoryEntries: categoryEntries?.items[0],
    authorData: authorEntries?.items[0],
    authorPosts,
    contextValue,
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const metaRobots = prepareRobotsMetadata(data.contextValue.url)

  const {
    authorData: {
      fields: { name },
    },
  } = data

  return [
    { title: `Explore career advice articles by writer ${name}` },
    {
      name: 'description',
      content: `Unlock career success with expert advice from ${name}. Explore articles on career planning and achieving your career aspirations.`,
    },
    { name: 'robots', content: metaRobots },
  ]
}

export default function Author() {
  const { authorData, categoryEntries, authorPosts } = useLoaderData<
    typeof loader
  >() as AuthorLoaderData

  return (
    <Layout headerBgColor={Colors.Neutral5}>
      <Layout.Content style={{ backgroundColor: Colors.White }}>
        <Blog categories={categoryEntries}>
          <AuthorHero fields={authorData.fields} />
          {authorPosts && (
            <PostsPreviewGroup
              posts={authorPosts.items}
              categories={categoryEntries}
              isAuthorPosts={true}
            />
          )}
          <BlogPaginate total={authorPosts?.total as number} perPage={PAGE_LIMIT} />
        </Blog>
      </Layout.Content>
    </Layout>
  )
}
